import { isEnforcedPlanFeature, type EnforcedPlanFeature } from '@bookora/shared';
import { isApiError } from '@bookora/ui';

import { isReadOnlyRefusal } from '@/layout/billing-refusal';

/** What a 402 FEATURE_NOT_IN_PLAN says (`featureNotInPlan`, CONTRACTS §4.2). */
export interface PlanRefusal {
  feature: EnforcedPlanFeature | null;
  upgradePlanKey: string | null;
  upgradePlanName: string | null;
}

export const FEATURE_NOT_IN_PLAN = 'FEATURE_NOT_IN_PLAN';

/** ONE notification key: ten refused saves raise one notification, not ten. */
export const PLAN_LOCK_NOTIFICATION_KEY = 'bookora-plan-lock';

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * The plan refusal an error carries, or null for anything else.
 *
 * A 402 that means THE CLINIC IS READ-ONLY (a lapsed subscription, a
 * suspension — `details.readOnly`, or one of the read-only codes) is NOT a
 * plan refusal, whatever its code: `BillingRefusalWatcher` owns that one, and
 * two notifications for one refused save would contradict each other («renew»
 * vs «upgrade»). The server answers read-only first (`readOnlyRefusal` before
 * the plan gate), so a read-only clinic never sees this.
 */
export function planRefusalOf(error: unknown): PlanRefusal | null {
  if (isReadOnlyRefusal(error)) return null;
  if (!isApiError(error) || error.statusCode !== 402) return null;
  if (error.code !== FEATURE_NOT_IN_PLAN) return null;
  const details = (error.details ?? {}) as Record<string, unknown>;
  const feature = isEnforcedPlanFeature(details.feature) ? details.feature : null;
  return {
    feature,
    upgradePlanKey: text(details.upgradePlanKey),
    upgradePlanName: text(details.upgradePlanName),
  };
}
